


































































































































































import Vue from "vue";
import DateInput from "@/components/DateInput.vue";
import PartnerSearch from "@/modules/partner/views/PartnerSearch.vue";
import Partner from "@/modules/partner/models/Partner";
import ApiSearch from "@/components/apiSearch/models/ApiSearch";
import { FilteringModel } from "@/services/filtering";
import { PreAccLookups } from "../models/PreAccounting";

interface Data {
  periodId: number | null;
  ready: boolean;
  defaultsApplied: boolean;
}

interface Methods {
  ensureFilters(): void;
  applyDefaultSelections(): void;
  onPeriodChange(): void;
  syncPeriodFromDates(): void;
  onPartnerSelected(partner: Partner): void;
}

interface Computed {
  filters: any;
  prefixOptions: any[];
  accStatusOptions: any[];
  bookTypeOptions: any[];
  periodOptions: any[];
  selectedPartnerId: number | undefined;
  mixMode: boolean;
}

interface Props {
  apiSearch: ApiSearch;
  lookups: PreAccLookups | null;
}

export default Vue.extend<Data, Methods, Computed, Props>({
  components: {
    DateInput,
    PartnerSearch,
  },

  props: {
    apiSearch: { type: Object as () => ApiSearch },
    lookups: { type: Object as () => PreAccLookups | null, default: null },
  },

  data() {
    return {
      periodId: null,
      ready: false,
      defaultsApplied: false,
    };
  },

  created() {
    this.ensureFilters();
    this.applyDefaultSelections();
    this.syncPeriodFromDates();
  },

  watch: {
    lookups() {
      this.applyDefaultSelections();
      this.syncPeriodFromDates();
    },
    // Az ApiSearchPage a mounted-jében ÚJ ApiSearch példányt hoz létre; ha a lookupok
    // ezt megelőzve értek volna ide, a defaultok a régi példányba kerültek — ilyenkor
    // az újra is alkalmazzuk (ez indítja az egyetlen kezdő keresést, ld. autoLoad=false)
    apiSearch() {
      this.defaultsApplied = false;
      // az új példány a régi (akár visszatöltött) kulcsokat megkapja — ilyenkor nincs alapérték-írás
      this.ensureFilters();
      this.applyDefaultSelections();
    },
  },

  methods: {
    ensureFilters() {
      const filter = (this.$props.apiSearch as any).filter as FilteringModel;
      if (!filter) return;

      const template = new FilteringModel()
        .value("startDate")
        .value("endDate")
        .value("partnerId")
        .value("storeIn")
        .value("gStoreIn")
        .value("storeOut")
        .value("kek")
        .multiSelect("selectedPrefixes", [], false)
        .multiSelect("selectedAccStatuses", [], false)
        .multiSelect("selectedBookTypes", [], false);

      let statusesCreated = false;
      Object.entries(template.filters).forEach(([key, value]) => {
        if (!filter.filters[key]) {
          Vue.set(filter.filters, key, value);
          if (key === "selectedAccStatuses") statusesCreated = true;
        }
      });

      // persistFilters: ha a státusz-pipák kulcsa már a visszatöltött modellből jött, a mentett
      // kijelölés marad, a K2D-alapállapot nem íródik rá (a folyószámla panel mintája).
      // Ilyenkor az alapérték-írás elmarad, így az ApiSearchPage szűrő-watchere sem indítja
      // az első lekérdezést (autoLoad=false) — a view-nak szólunk, hogy töltse be a listát.
      if (!statusesCreated) {
        this.defaultsApplied = true;
        this.$emit("filters-ready");
      }

      this.ready = true;
    },

    applyDefaultSelections() {
      if (this.defaultsApplied) return;
      if (!this.$props.lookups) return;
      if (!this.filters.selectedPrefixes) return;

      this.filters.selectedPrefixes.selectedOptions = this.prefixOptions.map((x: any) => x.value);
      // K2D-hű alapállapot (dw_status retrieveend): minden státusz pipálva, KIVÉVE
      // Feladva (külső) [4], Fők. rendsz. feladva [6] és Nem feladandó [9]
      this.filters.selectedAccStatuses.selectedOptions = this.accStatusOptions
        .map((x: any) => x.value)
        .filter((v: string) => v !== "4" && v !== "6" && v !== "9");
      this.filters.selectedBookTypes.selectedOptions = this.bookTypeOptions.map((x: any) => x.value);

      this.defaultsApplied = true;
    },

    onPeriodChange() {
      const period = (this.$props.lookups?.periods ?? []).find((x: any) => x.id === this.periodId);
      if (!period) return;
      this.filters.startDate.value = period.fromDate;
      this.filters.endDate.value = period.toDate;
    },

    // Visszatöltött (persistFilters) időszak-dátumoknál az időszak-választó is a megfelelő
    // periódusra álljon
    syncPeriodFromDates() {
      const from = this.filters.startDate?.value;
      const to = this.filters.endDate?.value;
      if (!from || !to) return;
      const period = (this.$props.lookups?.periods ?? []).find((x: any) => x.fromDate === from && x.toDate === to);
      if (period) this.periodId = period.id;
    },

    onPartnerSelected(partner: Partner) {
      this.filters.partnerId.value = partner && partner.id > 1 ? String(partner.id) : null;
    },
  },

  computed: {
    filters(): any {
      return (this.$props.apiSearch as any).filter?.filters ?? {};
    },

    prefixOptions(): any[] {
      // K2D-paritás: a megjelenített szöveg a magyar prefix (BEJÖ, ÚJGJ, ...),
      // a szűrő-érték a belső kód (GENI, GENO, MIX, ...)
      return (this.$props.lookups?.prefixes ?? []).map((x: any) => ({
        text: x.prefix,
        value: x.code,
      }));
    },

    accStatusOptions(): any[] {
      return (this.$props.lookups?.accStatuses ?? []).map((x: any) => ({
        text: x.name,
        value: String(x.id),
      }));
    },

    bookTypeOptions(): any[] {
      return (this.$props.lookups?.bookTypes ?? []).map((x: any) => ({
        text: x.name,
        value: String(x.id),
      }));
    },

    periodOptions(): any[] {
      return (this.$props.lookups?.periods ?? []).map((x: any) => ({
        text: x.nicName,
        value: x.id,
      }));
    },

    selectedPartnerId(): number | undefined {
      const value = this.filters.partnerId?.value;
      return value ? Number(value) : undefined;
    },

    mixMode(): boolean {
      return (
        this.filters.storeIn?.value === "true" ||
        this.filters.gStoreIn?.value === "true" ||
        this.filters.storeOut?.value === "true" ||
        this.filters.kek?.value === "true"
      );
    },
  },
});
